import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  const onForgot = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
    } catch {
      toast.error("Something went wrong — try again");
    } finally { setForgotBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex">
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-neutral-900 text-white p-12 relative grain">
        <Link to="/" className="flex items-center gap-2" data-testid="login-brand-link">
          <div className="w-5 h-5 bg-[#E54D2E]" />
          <span className="font-display text-lg font-bold">Runwell HQ</span>
        </Link>
        <div>
          <div className="mono text-xs text-neutral-500 mb-4">— WEEKLY BUSINESS RHYTHM</div>
          <h1 className="font-display text-5xl font-bold leading-tight">
            You built the company.<br /> Now install the operating system.
          </h1>
        </div>
        <div className="mono text-xs text-neutral-500">v0.1 — 2026</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6">
          <div>
            <div className="mono text-xs tracking-widest uppercase text-neutral-500">SIGN IN</div>
            <h2 className="font-display text-4xl font-bold mt-2">Welcome back.</h2>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
              className="rounded-sm" data-testid="login-email-input" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <button type="button" onClick={() => { setForgotOpen(true); setForgotEmail(email); setForgotSent(false); }} className="text-xs text-neutral-500 hover:text-[#E54D2E]" data-testid="forgot-password-link">Forgot password?</button>
            </div>
            <Input id="password" type="password" required value={password} onChange={(e)=>setPassword(e.target.value)}
              className="rounded-sm" data-testid="login-password-input" />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white rounded-sm btn-lift" data-testid="login-submit-btn">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
          <div className="text-sm text-neutral-600">
            No account? <Link to="/register" className="text-[#E54D2E] font-medium" data-testid="login-register-link">Create one</Link>
          </div>
        </form>

        <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Reset your password</DialogTitle></DialogHeader>
            {forgotSent ? (
              <div className="space-y-3" data-testid="forgot-sent-message">
                <p className="text-sm text-neutral-600">If <strong>{forgotEmail}</strong> has an account, a reset link is on its way. Check your inbox (and spam). The link expires in 1 hour.</p>
                <Button variant="outline" className="rounded-sm" onClick={() => setForgotOpen(false)} data-testid="forgot-close-btn">Close</Button>
              </div>
            ) : (
              <form onSubmit={onForgot} className="space-y-3">
                <div>
                  <Label htmlFor="forgot-email">Account email</Label>
                  <Input id="forgot-email" type="email" required value={forgotEmail} onChange={(e)=>setForgotEmail(e.target.value)} className="rounded-sm mt-1" data-testid="forgot-email-input" />
                </div>
                <Button type="submit" disabled={forgotBusy} className="w-full bg-[#E54D2E] hover:bg-[#C93E22] text-white rounded-sm" data-testid="forgot-submit-btn">
                  {forgotBusy ? "Sending…" : "Email me a reset link"}
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
